import { createContext, type PropsWithChildren, useContext } from 'react';
import { FormProvider } from 'react-hook-form';
import { useAppLanguage, useFormSearchQueries } from '@gov-nx/core/hooks';
import type { Nullable } from '@gov-nx/core/types';
import { useSearchParamsOperations } from '@gov-nx/web';
import { useRepresentationForm } from '../smart-forms/FormDefinition';
import type { EasyFormContextTypes } from './context.types';
import { getEasyFormParamsLink } from './utils';

const Context = createContext<Nullable<EasyFormContextTypes>>(null);

export function EasyFormContextProvider({
	children,
	formId,
}: PropsWithChildren<{ formId: string }>) {
	const { language } = useAppLanguage();
	const { deleteParam } = useSearchParamsOperations();

	const { easyFormData, formType, isFetching, isError } = useFormSearchQueries({
		formId,
	});

	const name = easyFormData?.nazev?.[language];
	const link = getEasyFormParamsLink(
		formId,
		formType,
		easyFormData?.sablonyReza
	);

	const representationForm = useRepresentationForm({
		link,
		nazev: name,
		isRepresentationAllowed: easyFormData?.zastupovani ?? false,
	});

	return (
		<Context.Provider
			value={{
				isFetching,
				isError,
				isNoData: !easyFormData,
				onModalClose: () => deleteParam('formId'),
				name,
				anotation: easyFormData?.anotace?.[language],
				formId,
				isModalFooterVisible: !!easyFormData || isFetching,
				...representationForm,
			}}>
			<FormProvider {...representationForm.form.formMethods}>
				{children}
			</FormProvider>
		</Context.Provider>
	);
}

export const useEasyFormContext = (): EasyFormContextTypes =>
	useContext(Context) as EasyFormContextTypes;
